const _excluded = ["type", "children", "className", "prefix", "suffix", "disableSuffixPadding", "label", "required", "success", "testid", "inputProps", "size", "menuMaxHeight", "width", "minWidth", "isMoney", "isPercentage", "SelectProps", "alwaysShrinkLabel", "onKeyDown", "onChange", "warning", "preventOutline", "select", "placeholder", "value", "isSignature", "displayValue", "inputRootClass"];
function _extends() { return _extends = Object.assign ? Object.assign.bind() : function (n) { for (var e = 1; e < arguments.length; e++) { var t = arguments[e]; for (var r in t) ({}).hasOwnProperty.call(t, r) && (n[r] = t[r]); } return n; }, _extends.apply(null, arguments); }
function _objectWithoutProperties(e, t) { if (null == e) return {}; var o, r, i = _objectWithoutPropertiesLoose(e, t); if (Object.getOwnPropertySymbols) { var n = Object.getOwnPropertySymbols(e); for (r = 0; r < n.length; r++) o = n[r], t.indexOf(o) >= 0 || {}.propertyIsEnumerable.call(e, o) && (i[o] = e[o]); } return i; }
function _objectWithoutPropertiesLoose(r, e) { if (null == r) return {}; var t = {}; for (var n in r) if ({}.hasOwnProperty.call(r, n)) { if (e.indexOf(n) >= 0) continue; t[n] = r[n]; } return t; }
function ownKeys(e, r) { var t = Object.keys(e); if (Object.getOwnPropertySymbols) { var o = Object.getOwnPropertySymbols(e); r && (o = o.filter(function (r) { return Object.getOwnPropertyDescriptor(e, r).enumerable; })), t.push.apply(t, o); } return t; }
function _objectSpread(e) { for (var r = 1; r < arguments.length; r++) { var t = null != arguments[r] ? arguments[r] : {}; r % 2 ? ownKeys(Object(t), !0).forEach(function (r) { _defineProperty(e, r, t[r]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(e, Object.getOwnPropertyDescriptors(t)) : ownKeys(Object(t)).forEach(function (r) { Object.defineProperty(e, r, Object.getOwnPropertyDescriptor(t, r)); }); } return e; }
function _defineProperty(e, r, t) { return (r = _toPropertyKey(r)) in e ? Object.defineProperty(e, r, { value: t, enumerable: !0, configurable: !0, writable: !0 }) : e[r] = t, e; }
function _toPropertyKey(t) { var i = _toPrimitive(t, "string"); return "symbol" == typeof i ? i : i + ""; }
function _toPrimitive(t, r) { if ("object" != typeof t || !t) return t; var e = t[Symbol.toPrimitive]; if (void 0 !== e) { var i = e.call(t, r || "default"); if ("object" != typeof i) return i; throw new TypeError("@@toPrimitive must return a primitive value."); } return ("string" === r ? String : Number)(t); }
import React, { forwardRef } from 'react';
import cn from 'classnames';
import { TextField as MuiTextField } from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import { ArrowDownFill } from "../../icons";
import { preventBadNumberInputKeys, preventBadNumberInputValues } from "../../input-management";
import { isValidNumberWithPrecisionOf2, isValidPercentageWithPrecisionOf2 } from "../../utils/money-input";
import colors from "../../foundations/colors.module.scss";
import typography from "../../foundations/typography.module.scss";
const baseFont = {
  fontFamily: typography.fontFamilyLight,
  color: colors.dark700,
  fontSize: typography.fontSizeP1
};
const signatureFont = {
  fontFamily: 'AlexBrush-Regular',
  fontWeight: 400,
  fontSize: '30px'
};
const defaultBorderColor = colors.dark900;
const hoverColor = colors.dark900;
const focusedColor = colors.purple800;
const errorColor = colors.red950;
const warningColor = colors.orange900;
const successColor = colors.green900;
const disabledColor = {
  text: colors.dark500,
  border: colors.dark300
};
const placeholderColor = colors.dark400;
const useStyles = makeStyles({
  root: {
    width: props => props.width || '100%',
    minWidth: props => props.minWidth || 0,
    '& .MuiSelect-select': {
      backgroundColor: 'transparent'
    }
  },
  inputRoot: _ref => {
    let {
      disableSuffixPadding
    } = _ref;
    return _objectSpread(_objectSpread({}, baseFont), {}, {
      // Disable input type=number arrows in Chrome, Safari, Edge, Opera
      '& input::-webkit-outer-spin-button, input::-webkit-inner-spin-button': {
        '-webkit-appearance': 'none',
        margin: 0
      },
      // Disable input type=number arrows in Firefox
      '& input[type=number]': {
        '-moz-appearance': 'textfield'
      },
      '& .MuiOutlinedInput-notchedOutline': {
        borderColor: defaultBorderColor,
        borderRadius: 4,
        '& span': {
          fontFamily: typography.fontFamilyLight
        }
      },
      '& .MuiOutlinedInput-inputAdornedStart': {
        paddingLeft: 8
      },
      '& .MuiOutlinedInput-inputAdornedEnd': {
        paddingRight: 8
      },
      '&.MuiOutlinedInput-adornedEnd': {
        paddingRight: disableSuffixPadding ? 0 : 14
      },
      '&:hover .MuiOutlinedInput-notchedOutline': {
        borderColor: hoverColor,
        borderWidth: 2
      },
      '&.Mui-focused': {
        '& .MuiOutlinedInput-notchedOutline': {
          borderColor: focusedColor
        }
      },
      '&.Mui-disabled': {
        color: disabledColor.text,
        '& .MuiOutlinedInput-notchedOutline': {
          borderColor: disabledColor.border
        },
        '& .MuiOutlinedInput-input': {
          color: disabledColor.text,
          '&::placeholder': _objectSpread(_objectSpread({}, baseFont), {}, {
            opacity: 0.5
          })
        }
      },
      '&.Mui-error.MuiOutlinedInput-root .MuiOutlinedInput-notchedOutline': {
        borderColor: errorColor
      },
      '&.MuiOutlinedInput-multiline': {
        minHeight: '46px',
        padding: '8px 0'
      }
    });
  },
  inputInput: _ref2 => {
    let {
      isSignature
    } = _ref2;
    return _objectSpread(_objectSpread({}, isSignature ? signatureFont : baseFont), {}, {
      padding: '4px 16px',
      lineHeight: '24px',
      '&::placeholder': _objectSpread(_objectSpread({}, isSignature ? signatureFont : baseFont), {}, {
        opacity: 0.5
      }),
      '&:-webkit-autofill': _objectSpread({}, baseFont),
      '&:autofill': _objectSpread({}, baseFont)
    });
  },
  inputInput_medium: {
    height: 38,
    '&.MuiSelect-select': {
      lineHeight: '38px'
    }
  },
  inputInput_small: {
    height: 32,
    '&.MuiSelect-select': {
      lineHeight: '32px'
    }
  },
  label: _objectSpread(_objectSpread({}, baseFont), {}, {
    '&.Mui-disabled': {
      color: disabledColor.text
    },
    '&.Mui-focused': {
      color: focusedColor
    },
    '&.Mui-error': {
      color: baseFont.color,
      '&.MuiInputLabel-shrink': {
        color: errorColor
      },
      '& .sg-tf-asterix': {
        color: errorColor
      }
    }
  }),
  label_medium: {
    transform: 'translate(16px, 16px) scale(1)'
  },
  label_small: {
    transform: 'translate(16px, 13px) scale(1)'
  },
  ellipsis: {
    overflow: 'hidden',
    whiteSpace: 'nowrap',
    textOverflow: 'ellipsis'
  },
  helperText: _objectSpread(_objectSpread({}, baseFont), {}, {
    fontSize: 12,
    marginTop: 0,
    marginLeft: 0,
    position: 'absolute',
    bottom: 0,
    left: 0,
    transform: 'translateY(100%)',
    lineHeight: 'normal',
    '&.Mui-disabled': {
      color: disabledColor.text
    },
    '&.Mui-error': {
      color: errorColor
    }
  }),
  success: {
    '& .MuiOutlinedInput-notchedOutline': {
      borderColor: "".concat(successColor, " !important")
    },
    '& .MuiInputLabel-outlined.MuiInputLabel-shrink': {
      color: successColor
    },
    '& .sg-tf-asterix': {
      color: successColor
    }
  },
  warning: {
    '& .MuiOutlinedInput-notchedOutline': {
      borderColor: "".concat(warningColor, " !important")
    },
    '& .MuiInputLabel-outlined.MuiInputLabel-shrink': {
      color: warningColor
    },
    '& .sg-tf-asterix': {
      color: warningColor
    }
  },
  menu: {
    maxHeight: props => props.menuMaxHeight
  },
  noOutline: {
    '& .MuiOutlinedInput-notchedOutline': {
      border: 'none'
    }
  },
  selectPlaceholder: {
    color: placeholderColor
  }
});
export const TextField = /*#__PURE__*/forwardRef((props, ref) => {
  var _props$InputProps, _props$InputProps2, _props$InputProps3;
  const {
      type,
      children,
      className,
      prefix,
      suffix,
      disableSuffixPadding,
      label,
      required,
      success,
      testid,
      inputProps,
      size = 'medium',
      menuMaxHeight,
      width,
      minWidth,
      isMoney,
      isPercentage,
      SelectProps,
      alwaysShrinkLabel,
      onKeyDown,
      onChange,
      warning,
      preventOutline,
      select,
      placeholder,
      value,
      isSignature,
      displayValue,
      inputRootClass
    } = props,
    rest = _objectWithoutProperties(props, _excluded);
  const styles = useStyles({
    menuMaxHeight,
    width,
    minWidth,
    disableSuffixPadding,
    isSignature
  });
  const actualType = isMoney || isPercentage ? 'number' : type;
  const isNumberType = actualType === 'number';
  const tid = testid || props['data-testid'];
  const shouldHackSelectWithPlaceholder = select && placeholder;
  const extraSelectProps = shouldHackSelectWithPlaceholder ? {
    displayEmpty: true,
    renderValue: value => {
      if (Array.isArray(value) ? value.length > 0 : value) {
        const selectedChild = React.Children.toArray(children).find(child => /*#__PURE__*/React.isValidElement(child) && child.props.value === value);
        const selectLabel = /*#__PURE__*/React.isValidElement(selectedChild) ? selectedChild.props.label || selectedChild.props.children : value;
        return /*#__PURE__*/React.createElement("div", {
          "data-testid": "".concat(tid, "-label"),
          className: styles.ellipsis
        }, displayValue || selectLabel);
      } else {
        return /*#__PURE__*/React.createElement("div", {
          "data-testid": "".concat(tid, "-placeholder"),
          className: styles.selectPlaceholder
        }, placeholder);
      }
    }
  } : {};
  return /*#__PURE__*/React.createElement(MuiTextField, _extends({
    innerRef: ref,
    "data-testid": tid
  }, rest, {
    select: select,
    placeholder: placeholder,
    value: value,
    type: actualType,
    className: cn(className, {
      [styles.success]: success,
      [styles.warning]: warning,
      [styles.noOutline]: preventOutline
    }, styles.root),
    variant: "outlined",
    label: required && label ? /*#__PURE__*/React.createElement(React.Fragment, null, label, /*#__PURE__*/React.createElement("span", {
      className: "sg-tf-asterix"
    }, "*")) : label,
    onChange: isNumberType ? e => {
      const newValue = e.currentTarget.value;
      if (Number.isNaN(+newValue)) {
        return;
      }
      preventBadNumberInputValues(e);
      if (isMoney && !isValidNumberWithPrecisionOf2(newValue)) {
        return;
      }
      if (isPercentage && !isValidPercentageWithPrecisionOf2(newValue)) {
        return;
      }
      onChange && onChange(e);
    } : onChange,
    onKeyDown: isNumberType ? e => {
      preventBadNumberInputKeys(e, inputProps && inputProps.min != null && inputProps.min >= 0);
      onKeyDown && onKeyDown(e);
    } : undefined,
    InputProps: {
      ref: (_props$InputProps = props.InputProps) === null || _props$InputProps === void 0 ? void 0 : _props$InputProps.ref,
      startAdornment: prefix || ((_props$InputProps2 = props.InputProps) === null || _props$InputProps2 === void 0 ? void 0 : _props$InputProps2.startAdornment),
      endAdornment: suffix || ((_props$InputProps3 = props.InputProps) === null || _props$InputProps3 === void 0 ? void 0 : _props$InputProps3.endAdornment),
      required,
      classes: {
        root: cn(styles.inputRoot, inputRootClass),
        input: cn(styles.inputInput, styles["inputInput_".concat(size)])
      }
    },
    SelectProps: _objectSpread(_objectSpread({
      IconComponent: ArrowDownFill,
      MenuProps: {
        anchorOrigin: {
          vertical: 'bottom',
          horizontal: 'center'
        },
        transformOrigin: {
          vertical: 'top',
          horizontal: 'center'
        },
        getContentAnchorEl: null,
        classes: {
          paper: styles.menu
        }
      }
    }, extraSelectProps), SelectProps) // eslint-disable-line @typescript-eslint/no-explicit-any
    ,
    InputLabelProps: {
      ['data-testid']: tid && "".concat(tid, "-label"),
      classes: {
        outlined: cn(styles.label, styles["label_".concat(size)])
      },
      shrink: alwaysShrinkLabel || shouldHackSelectWithPlaceholder || undefined
    } // eslint-disable-line @typescript-eslint/no-explicit-any
    ,
    inputProps: _objectSpread({
      ['data-testid']: tid && "".concat(tid, "-input"),
      step: actualType === 'number' ? 'any' : undefined
    }, inputProps),
    FormHelperTextProps: {
      ['data-testid']: tid && "".concat(tid, "-helper-text"),
      classes: {
        contained: styles.helperText
      }
    } // eslint-disable-line @typescript-eslint/no-explicit-any
  }), children);
});